/**
 * Authentication state management
 * Redux slice for managing authentication state
 * @module Auth/Reducer
 */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AuthState } from '../../../interfaces/reducers/auth';
import { LoginDataType } from '../../../interfaces/api/auth/login';

/**
 * Initial authentication state
 * @type {AuthState}
 */
const initialState: AuthState = {
  currentUser: null,
};

/**
 * Authentication Redux slice
 * Manages user authentication state, profession updates, and voice interaction flags
 */
export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    /**
     * Sets the current user after successful login
     * @param {AuthState} state - Current auth state
     * @param {PayloadAction<LoginDataType | null>} action - Login action with user data payload
     */
    login: (state, action: PayloadAction<LoginDataType | null>) => {
      state.currentUser = action.payload;
    },

    /**
     * Clears user data on logout
     * @param {AuthState} state - Current auth state
     */
    logout: (state) => {
      state.currentUser = null;
    },

    /**
     * Updates user profession and filters tabs accordingly
     * @param {AuthState} state - Current auth state
     * @param {PayloadAction<number>} action - Action with profession ID payload
     */
    updateProfession: (state, action: PayloadAction<number>) => {
      if (state.currentUser && state.currentUser.user) {
        const selectedProfessionId = action.payload;
        // Update user profession info
        state.currentUser.user.profession_id = selectedProfessionId;
        state.currentUser.user.profession_updated = 1;
        state.currentUser.user.profession_name =
          state.currentUser.professions.find((p) => p.id === selectedProfessionId)?.name || '';

        // Filter tabs based on selected profession
        if (state.currentUser.tabs) {
          state.currentUser.tabs = state.currentUser.tabs.filter(
            (tab) => tab.profession_id === null || tab.profession_id === selectedProfessionId,
          );
        }
      }
    },

    /**
     * Marks voice tab interaction as played
     * @param {AuthState} state - Current auth state
     */
    setIsVoiceTabInteractionPlayed: (state) => {
      state.isVoiceTabInteractionPlayed = true;
    },

    /**
     * Resets voice-related flags
     * @param {AuthState} state - Current auth state
     */
    resetTabsVoice: (state) => {
      state.isVoiceTabNewsPlayed = false;
      state.isVoiceTabInteractionPlayed = false;
    },
  },
});

// Action creators are generated for each case reducer function
export const { login, logout, updateProfession, setIsVoiceTabInteractionPlayed, resetTabsVoice } = authSlice.actions;

export default authSlice.reducer;
